import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressBarModule } from 'primeng/progressbar';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { apps } from '../../../constants';
import { DateMomentPipe } from '../../../pipes';
import { DebugLoggerService } from '../../../services/debug-logger.service';
import { AppStateHolder, TimeLine } from '../../../shared';
import { UserProjectsService } from './user-projects.service';


@Component({
    selector: 'calendar-user-projects',
    templateUrl: 'user-projects.component.html',
    styleUrls: ['./user-projects.redesign.scss'],
    standalone: true,
    imports: [TooltipModule, ProgressBarModule, ButtonModule, SkeletonModule, OverlayPanelModule, PrimeTemplate, DateMomentPipe, TranslateModule, TagModule]
})

export class UserProjectsComponent implements OnInit, OnChanges, OnDestroy {
  private router = inject(Router);
  private timeLine = inject(TimeLine);
  appStateHolder = inject(AppStateHolder);
  private service = inject(UserProjectsService);
  private translate = inject(TranslateService);
  private liveAnnouncer = inject(LiveAnnouncer);
  private _debugLogger = inject(DebugLoggerService);

  @ViewChild('activities') activitiesPanel: OverlayPanel;

  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;
  @Input() params: string;
  @Input() changedPjParams: any;
  @Input() widgetPosition: any;

  @Output() pjpopup: EventEmitter<{ widx: number, pop: boolean, params: any }> = new EventEmitter();

  // State configuration for tags with accessible labels
  stateConfig: { [key: number]: { severity: 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast'; icon: string; labelKey: string } } = {
    0: { severity: 'danger', icon: 'pi pi-clock', labelKey: 'widgets.user-projects.state-open' },
    1: { severity: 'warning', icon: 'pi pi-bolt', labelKey: 'widgets.user-projects.state-in-progress' },
    2: { severity: 'success', icon: 'pi pi-check-circle', labelKey: 'widgets.user-projects.state-completed' }
  };

  status: string;
  projects: any[];
  openOptions: boolean = false;

  finalDate: string;
  initialDate: string;
  requestsSubscribe: Subscription;

  shortDate: any = [{ format: "L" }];

  selectedProject: any;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
  }

  ngOnInit(): void {
    this._debugLogger.logLifecycle('UserProjects', 'ngOnInit');
    if (!this.modify) {
      this.timeLine.init();
      this.loadProjects(this.params);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (this.changedPjParams != undefined && this.idx == this.changedPjParams.indx) {
        this.loadProjects(this.changedPjParams.params);
      }
    }
  }

  /**
   * Handles activity selection and navigation
   * @param activity The activity to navigate to
   */
  onSelectActivity(activity: any): void {
    this._debugLogger.logAction('UserProjects', 'selectActivity', { id: activity.id });
    const url = this.appStateHolder.getAppUrl(apps.calendar);
    this.appStateHolder.setAppNavbarClass(apps.calendar);

    if (url.length === 0) {
      const link = ['/activity', activity.id];
      this._debugLogger.logNavigation(link.join('/'));
      this.router.navigate(link);
      return;
    }

    const link = ['/app', `${url}/activity/${activity.id}`];
    this._debugLogger.logNavigation(link.join('/'));
    this.router.navigate(link);
  }

  /**
   * Loads projects based on filter parameters
   * @param params JSON string of filter parameters
   */
  loadProjects(params: string): void {
    this._debugLogger.logAction('UserProjects', 'loadProjects', { initialDate: this.initialDate, finalDate: this.finalDate });
    if (params) {
      let prms = JSON.parse(params);
      prms = prms.params ? JSON.parse(prms.params) : prms;

      switch (prms.date) {
        case 'Two Months':
          this.dataClick(1);
          break;
        case 'Three Months':
          this.dataClick(2);
          break;
        default:
          this.dataClick(0);
          break;
      }

      this.requestsSubscribe = this.service.getUserProjects(this.initialDate, this.finalDate, prms.state)
        .subscribe(result => {
          this._debugLogger.logAction('UserProjects', 'projects loaded', { count: result?.length });
          this.projects = result;
          this.announceProjectsLoaded();
        });
    } else {
      this.dataClick(0);
      this.service.getUserProjects(this.initialDate, this.finalDate, "All")
        .subscribe(result => {
          this._debugLogger.logAction('UserProjects', 'projects loaded', { count: result?.length });
          this.projects = result;
          this.announceProjectsLoaded();
        });
    }
  }

  /**
   * Handles project selection and navigation
   * @param project The project to navigate to
   */
  onSelectProject(project: any): void {
    this._debugLogger.logAction('UserProjects', 'selectProject', { id: project.id });
    const url = this.appStateHolder.getAppUrl(apps.calendar);
    this.appStateHolder.setAppNavbarClass(apps.calendar);

    if (url.length === 0) {
      const link = ['/project', project.id];
      this._debugLogger.logNavigation(link.join('/'));
      this.router.navigate(link);
      return;
    }

    const link = ['/app', `${url}/project/${project.id}`];
    this._debugLogger.logNavigation(link.join('/'));
    this.router.navigate(link);
  }

  /**
   * Toggles the options panel
   */
  options(): void {
    this.openOptions = !this.openOptions;
  }

  /**
   * Sets date range based on selected period type
   * @param dateType 0=current month, 1=two months, 2=three months, 3=all time
   */
  dataClick(dateType: number): void {
    switch (dateType) {
      case 0:
        this.initialDate = this.timeLine.month.date.getFullYear() + "/" + (this.timeLine.month.date.getMonth() + 1) + "/" + this.timeLine.month.date.getDate();
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "/" + (this.timeLine.month.lastDate.getMonth() + 1) + "/" + this.timeLine.month.lastDate.getDate();
        break;
      case 1:
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        this.initialDate = d.getFullYear() + '/' + (d.getMonth() + 1) + '/' + 1;
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "/" + (this.timeLine.month.lastDate.getMonth() + 1) + "/" + this.timeLine.month.lastDate.getDate();
        break;
      case 2:
        const dat = new Date();
        dat.setMonth(dat.getMonth() - 2);
        this.initialDate = dat.getFullYear() + '/' + (dat.getMonth() + 1) + '/' + 1;
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "/" + (this.timeLine.month.lastDate.getMonth() + 1) + "/" + this.timeLine.month.lastDate.getDate();
        break;
      case 3:
        this.initialDate = null;
        this.finalDate = null;
    }
  }

  /**
   * Opens the project popup for editing widget settings
   */
  pjpop(): void {
    this._debugLogger.logEvent('UserProjects', 'popup emitted', { idx: this.idx });
    if (this.changedPjParams == undefined) {
      this.sendParams();
    } else {
      const val = this.appStateHolder.projectParams.filter(r => r.indx == this.idx);

      if (val.length > 0) {
        this.pjpopup.next({ widx: this.idx, pop: true, params: JSON.parse(val[0].params) });
      } else {
        this.sendParams();
      }
    }
  }

  /**
   * Sends parameters to parent component
   */
  sendParams(): void {
    if (this.params) {
      this.pjpopup.next({ widx: this.idx, pop: true, params: JSON.parse(this.params) });
    } else {
      this.pjpopup.next({ widx: this.idx, pop: true, params: this.params });
    }
  }

  // ============================================
  // ACCESSIBILITY HELPER METHODS
  // ============================================

  /**
   * Gets the accessible label for project state
   * @param state The numeric state value (0=open, 1=in progress, 2=completed)
   * @returns Translated state label
   */
  getStateLabel(state: number): string {
    const config = this.stateConfig[state];
    return config ? this.translate.instant(config.labelKey) : '';
  }

  /**
   * Gets the accessible date range label for screen readers
   * @returns Translated date range description
   */
  getDateRangeLabel(): string {
    if (!this.initialDate || !this.finalDate) {
      return '';
    }
    return this.translate.instant('widgets.user-projects.date-range-label', {
      startDate: this.initialDate,
      endDate: this.finalDate
    });
  }

  /**
   * Gets comprehensive accessible label for a project item
   * @param project The project object
   * @returns Complete project description for screen readers
   */
  getProjectAriaLabel(project: any): string {
    return this.translate.instant('widgets.user-projects.project-item-label', {
      title: project.title,
      owner: project.ownerFullName,
      date: project.referenceDate,
      progress: project.progress
    });
  }

  /**
   * Handles activities panel toggle with accessibility support
   * @param project The project to show activities for
   * @param event The click event
   */
  onActivitiesToggle(project: any, event: Event): void {
    this.selectedProject = project;
    this.activitiesPanel.toggle(event);
  }

  /**
   * Handles panel show event - announces to screen readers
   */
  onPanelShow(): void {
    if (this.selectedProject) {
      const message = this.translate.instant('widgets.user-projects.activities-panel-description', {
        title: this.selectedProject.title
      });
      this.liveAnnouncer.announce(message, 'polite');
    }
  }

  /**
   * Handles panel hide event - announces to screen readers
   */
  onPanelHide(): void {
    this.liveAnnouncer.announce(
      this.translate.instant('widgets.user-projects.activities-panel-title') + ' ' + 'closed',
      'polite'
    );
  }

  /**
   * Announces when projects have been loaded
   */
  private announceProjectsLoaded(): void {
    if (this.projects && this.projects.length > 0) {
      const message = `${this.projects.length} ${this.translate.instant('widgets.user-projects.title')} loaded`;
      this.liveAnnouncer.announce(message, 'polite');
    } else if (this.projects && this.projects.length === 0) {
      this.liveAnnouncer.announce(
        this.translate.instant('widgets.user-projects.no-projects-message'),
        'polite'
      );
    }
  }

  ngOnDestroy(): void {
    if (this.requestsSubscribe) {
      this.requestsSubscribe.unsubscribe();
    }
  }
}
